import { useState, useEffect, useContext, useImperativeHandle } from "react";
import { useReactFlow } from "reactflow";
import { useDispatch, useSelector } from "react-redux";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";

import { updateFlowSteps } from "redux/actions/flowDesigner/flowsActionCreator";
import { setComponentPropsPanelTitle } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";
import { THEME_COLORS } from "constants/theme";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { Actions } from "flexlayout-react";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { COMMON_COMPONENT_TYPES, NODE_TYPES } from "constants/flowDesigner";

const PropertiesTab = ({ selectedNode, flowElement, tabId, onDirtyChange, ref }) => {
   const { getNode, getNodes, setNodes } = useReactFlow();
   const dispatch = useDispatch();
   const { autoSaveFlow } = useContext(TabContextApi);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const tabState = useSelector((store) => store?.designer?.componentPropsPanel?.tabs[tabId] || {});
   const { componentPropsPanelTitle } = tabState;
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const originalTitle = componentPropsPanelTitle || "";
   const [form, setForm] = useState({ title: originalTitle, description: "", variableReference: "" });
   const [initialForm, setInitialForm] = useState(form);
   const { title, description, variableReference } = form;
   const { model } = useFlexLayout();
   const componentKey = selectedNode?.data?.componentKey || "undefined";
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const themeMode = useSelector((s) => s.portal.settings.themeMode);
   const isDark = themeMode === "dark";
   const isDirty = Object.keys(form).some((field) => form[field] !== initialForm[field]);
   const supportsVariableReference = selectedNode?.key !== COMMON_COMPONENT_TYPES.VARIABLES && selectedNode?.type !== NODE_TYPES.LABEL;
   const getOutputReferenceOptions = (variableNode) =>
      Object.entries(variableNode.data?.flowElement?.data ?? {})
         .filter(([field, name]) => field.startsWith("reference_var_") && name?.value)
         .map(([, name]) => ({
            value: `variables.${variableNode.data.componentKey}.${name.value}`,
            label: `${name.value} (${variableNode.data.componentKey})`
         }));
   const variableReferenceOptions = [
      { value: "", label: "None" },
      ...getNodes()
         .filter((node) => node.data?.type === COMMON_COMPONENT_TYPES.VARIABLES)
         .flatMap(getOutputReferenceOptions)
   ];

   const isSaveDisabled = title.trim() === "" || !isDirty || !allowEditWorkflow(isReadOnlyAccess);

   const stickyBarStyles = {
      position: "sticky",
      bottom: 0,
      width: "100%",
      backgroundColor: isDark ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
      display: "flex",
      justifyContent: "flex-end",
      gap: 2,
      padding: "8px 0 8px 0",
      zIndex: 50,
      "& button": { flex: 1, maxWidth: "100px", minWidth: 0 }
   };

   const handleSave = () => {
      const node = getNode(selectedNode?.elementId);
      if (!node || !allowEditWorkflow(isReadOnlyAccess)) return;

      const nodes = getNodes();
      const updatedNodeData = {
         ...node.data,
         customLabel: title,
         description: { value: description },
         descriptionUpdatedAt: Date.now()
      };
      if (variableReference) updatedNodeData.variable_reference = variableReference;
      else delete updatedNodeData.variable_reference;
      const updatedNode = {
         ...node,
         data: updatedNodeData
      };
      const updatedNodes = nodes.map((n) => (n.id === selectedNode?.elementId ? updatedNode : n));
      setNodes(updatedNodes);

      const element = { ...flowElement, id: updatedNode.id, data: updatedNode.data };
      dispatch(updateFlowSteps(element));
      dispatch(setComponentPropsPanelTitle(tabId, title));

      const tabNode = model.getNodeById(tabId);
      if (tabNode) {
         model.doAction(Actions.updateNodeAttributes(tabId, { name: title }));
      }

      autoSaveFlow(null, null, updatedNodes);
      setInitialForm({ ...form });
   };

   useImperativeHandle(
      ref,
      () => ({
         reset: () => {
            setForm({ ...initialForm });
         }
      }),
       [initialForm]
   );

   useEffect(() => {
      const node = getNode(selectedNode?.elementId);
      const desc = node?.data?.description?.value ?? flowElement?.data?.description?.value ?? "";
      const reference = node?.data?.variable_reference ?? "";
      const initialValues = { title: originalTitle, description: desc, variableReference: reference };
      setForm(initialValues);
      setInitialForm(initialValues);
   }, [selectedNode, flowElement, originalTitle]);

   useEffect(() => {
      onDirtyChange?.(isDirty);
   }, [isDirty]);

   return (
      <SoftBox sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
         <SoftBox sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            <SoftBox sx={{ mb: 2 }}>
               <SoftTypography fontSize="14px !important" fontWeight="normal">
                  Name
               </SoftTypography>
               <SoftInput value={title} onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))} placeholder="Name" required disabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)} />
               <SoftTypography variant="caption" color="text" sx={{ ml: 0.5, fontSize: "13px", fontStyle: "italic" }}>
                  Node ID: {componentKey}
               </SoftTypography>
            </SoftBox>

            {supportsVariableReference && (
               <SoftBox sx={{ mb: 2 }}>
                  <SoftTypography fontSize="14px !important" fontWeight="normal">
                     Variable Reference
                  </SoftTypography>
                  <SoftSelect
                     value={variableReferenceOptions.find((option) => option.value === variableReference) || variableReferenceOptions[0]}
                     options={variableReferenceOptions}
                     onChange={(option) => setForm((current) => ({ ...current, variableReference: option?.value || "" }))}
                     isSearchable={false}
                     isDisabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}
                  />
               </SoftBox>
            )}

            <SoftBox sx={{ mb: 2 }}>
               <SoftTypography fontSize="14px !important" fontWeight="normal">
                  Description
               </SoftTypography>
               <SoftInput
                  multiline
                  minRows={3}
                  maxRows={10}
                  value={description}
                  onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
                  placeholder="Enter description..."
                  disabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}
               />
            </SoftBox>
         </SoftBox>

         {!isFlowActive && (
            <SoftBox sx={stickyBarStyles}>
               <SoftButton variant="outlined" color="info" onClick={() => onDirtyChange?.("cancel")}>
                  Cancel
               </SoftButton>
               <SoftButton variant="contained" color="primary" disabled={isSaveDisabled} onClick={handleSave}>
                  Save
               </SoftButton>
            </SoftBox>
         )}
      </SoftBox>
   );
};

export default PropertiesTab;
